import { FC, useEffect, useState } from 'react'
import { RenderIf } from 'components/RenderIf'
import { Button } from 'featureSliced/entities/Button'
import { useMySelector } from 'featureSliced/shared/hooks/useMySelector'
import { useIsProductSoldOut } from 'featureSliced/shared/hooks/useIsProductSoldOut'
import { useMyDispatch } from 'featureSliced/shared/hooks/useMyDispatch'
import { AmountHandler } from 'featureSliced/widgets/AmountHandler'
import { getAmountConfig } from 'featureSliced/shared/utils/weightSteps'
import { handleLocalStorage } from 'featureSliced/shared/hooks/handleLocalStorage'
import {
  getItemLeadDays,
  isItemLeadDaysExceeded,
} from 'featureSliced/shared/utils/itemLeadDays'
import {
  articleHeadroom,
  getArticleBounds,
} from 'featureSliced/shared/utils/quantityBounds'

import { QRMBucketSlice } from 'featureSliced/shared/store/reducers/QRMenuBucketReducer'
import { TableStatuses } from 'featureSliced/shared/store/reducers/QRMenuTableReducer/types'

import {
  DeleteFromBucketButton,
  Divider,
  FooterActions,
  FooterContainer,
} from '../ComonProduct/styled'
import trashCan from 'assets/trashCan.svg'
import {
  IBucketProduct,
  IProductWithBucketProduct,
} from 'featureSliced/shared/store/reducers/QRMenuBucketReducer/types'
import { useTranslations } from 'featureSliced/shared/hooks/useTranslations'

type UpdateProductFooterProps = {
  product: IProductWithBucketProduct
  bucketProduct: IBucketProduct
  closeProduct: () => void
  currentAmount: number
  setCurrentAmount: (value: number) => void
}

export const UpdateProductFooter: FC<UpdateProductFooterProps> = ({
  product,
  bucketProduct,
  closeProduct,
  currentAmount,
  setCurrentAmount,
}) => {
  // Hooks
  const t = useTranslations()
  const dispatch = useMyDispatch()
  const addProductToBucket = QRMBucketSlice.actions.addProductToBucket
  const removeProductFromBucket = QRMBucketSlice.actions.removeProductFromBucket

  // Store
  const bucketLines = useMySelector((state) => state.bucket.products)
  const tableStatus = useMySelector((state) => state.qrMenuTable.tableStatus)
  const bookingVariant = useMySelector((state) => state.app.menu.bookingVariant)
  const disabledByMobileWaiter = useMySelector(
    (state) => state.app.menu.disabledByMobileWaiter
  )

  // Functions
  const amountConfig = getAmountConfig(product.unitSellCode)
  const orderMin = product.ClickCollectOrderMin ?? amountConfig.minAmount
  const isSoldOut = useIsProductSoldOut(product)
  const isLeadDaysExceeded = isItemLeadDaysExceeded({
    leadDays: getItemLeadDays(product.ClickCollectLeadDaysCount),
    pickUpTime: handleLocalStorage('pickUpTime'),
  })

  const weighed = product.unitSellCode === 2 || product.unitSellCode === 3
  // Per-article max across all cart lines (rmh-441): this line may take what the
  // article's OTHER lines leave of the max.
  const articleBounds = getArticleBounds(product)
  const headroom = articleHeadroom(
    articleBounds,
    bucketLines,
    bucketProduct.productId,
    bucketProduct.uniqueId
  )
  // zero headroom must not read as "no cap" (AmountHandler treats 0 as uncapped):
  // the stepper stays free, but an INCREASE past the headroom cannot be saved —
  // decreases always can (they reduce the violation)
  const updateBlocked =
    headroom !== null &&
    currentAmount > headroom &&
    currentAmount > bucketProduct.count

  // Editing never goes below the order minimum (QRM-649): the old "step from the
  // minimum drops to 0" removal path is retired — removal is the explicit delete
  // button (weight) or the cart line's remove control.
  const handleDecreaseAmount = (value: number) => {
    setCurrentAmount(value < orderMin ? orderMin : value)
  }

  const deleteFromBucket = () => {
    dispatch(removeProductFromBucket(bucketProduct.uniqueId))
    closeProduct()
  }

  const updateProduct = () => {
    const productToAdd = {
      ...bucketProduct,
      count: currentAmount,
      articleBounds,
    }
    dispatch(addProductToBucket(productToAdd))
    closeProduct()
  }

  return (
    <RenderIf
      key={`${navigator.onLine}${bookingVariant}${disabledByMobileWaiter}${tableStatus}${isLeadDaysExceeded}${isSoldOut}`}
      condition={
        navigator.onLine &&
        !isLeadDaysExceeded &&
        !isSoldOut &&
        (process.env.REACT_APP_IS_CLIC === '1' ||
          (bookingVariant === 2 &&
            !disabledByMobileWaiter &&
            tableStatus !== TableStatuses.BUSY))
      }
    >
      <Divider stacked={Boolean(amountConfig.coarseStep)} />
      <FooterContainer stacked={Boolean(amountConfig.coarseStep)}>
        <AmountHandler
          multiplier={amountConfig.multiplier}
          step={amountConfig.step}
          coarseStep={amountConfig.coarseStep}
          fineLabel={amountConfig.fineLabel}
          coarseLabel={amountConfig.coarseLabel}
          unit={amountConfig.unit}
          currentValue={currentAmount}
          onDecrease={handleDecreaseAmount}
          onIncrease={setCurrentAmount}
          minValue={orderMin}
          maxValue={headroom || undefined} // article max minus the cart's other lines (rmh-441); 0 would read as uncapped
        />
        {weighed ? (
          <FooterActions stacked={Boolean(amountConfig.coarseStep)}>
            <DeleteFromBucketButton
              type="button"
              aria-label={t('buttons.remove')}
              title={t('buttons.remove')}
              onClick={deleteFromBucket}
            >
              <img src={trashCan} alt="" />
            </DeleteFromBucketButton>
            <Button
              buttonType="common"
              disabled={tableStatus === TableStatuses.BUSY || updateBlocked}
              width="100%"
              onClick={updateProduct}
            >
              {t('buttons.update')}
            </Button>
          </FooterActions>
        ) : (
          <Button
            buttonType="common"
            disabled={tableStatus === TableStatuses.BUSY || updateBlocked}
            width="100%"
            onClick={updateProduct}
          >
            {t('buttons.update')}
          </Button>
        )}
      </FooterContainer>
    </RenderIf>
  )
}
